<template>
  <div ref="rootEl" class="metronome-visual" :class="{ playing: isSwinging }" :style="{ '--beat-count': beatsCount }">
    <div class="stage">
      <!-- 摆锤扫过的圆弧轨道（装饰，静态） -->
      <svg class="arc-track" viewBox="0 0 240 150" aria-hidden="true">
        <path d="M 33.6 132.5 A 110 110 0 0 1 206.4 132.5" />
      </svg>
      <!-- 连续摆：绕顶部支点旋转，角度由 rAF 每帧写入 CSS 变量 -->
      <div class="rod">
        <div class="weight">
          <div class="weight-line"></div>
        </div>
      </div>
      <div class="pivot"></div>
    </div>

    <!-- 拍点：当前拍随音频时钟脉冲呼吸发光 -->
    <div class="dots">
      <span v-for="n in beatsCount" :key="n" class="dot"
        :class="{ current: currentBeat === n, accent: n === 1 }">{{ n }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import type { BeatTimeline } from '@/composables/useMetronome'

const props = defineProps<{
  beatsCount: number
  currentBeat: number
  isSwinging: boolean
  active: boolean
  /** 音频时钟节拍时间线，缺省时回退到内部相位钟（无 Web Audio 环境） */
  getTimeline?: (() => BeatTimeline | null) | null
  /** 一拍时长（秒），仅在无音频时间线的回退相位钟模式下驱动节奏 */
  beatInterval?: number
}>()

const rootEl = ref<HTMLElement | null>(null)

/** 摆幅峰值（度） */
const AMPLITUDE = 30
/** 停摆回中的时间常数（秒） */
const SETTLE_TAU = 0.35
/** 拍点发光衰减时间常数（秒） */
const PULSE_TAU = 0.16

let rafId = 0
let lastTs = 0
let angle = 0 // 度
let pulse = 0 // 0..1
let theta = 0 // 回退模式下的连续相位（弧度）

const sideOf = (index: number) => (index % 2 === 0 ? 1 : -1)

function step(ts: number) {
  const dt = lastTs ? Math.min((ts - lastTs) / 1000, 0.1) : 0
  lastTs = ts

  const tl = props.getTimeline ? props.getTimeline() : null

  if (props.isSwinging && tl && tl.points.length >= 2) {
    // —— 音频时钟同步：在相邻两拍之间做半周期余弦插值 ——
    const { now, points } = tl
    let i = 0
    while (i < points.length - 1 && points[i + 1]!.time <= now) i++
    const p0 = points[i]!
    const p1 = points[Math.min(i + 1, points.length - 1)]!
    const span = Math.max(p1.time - p0.time, 1e-4)
    let x = (now - p0.time) / span
    x = x < 0 ? 0 : x > 1 ? 1 : x
    // x=0 落在 p0 的极端（刚发声），x=1 落到 p1 的极端（下一拍发声），中途经过中心
    angle = AMPLITUDE * sideOf(p0.index) * Math.cos(Math.PI * x)
    pulse = Math.exp(-Math.max(now - p0.time, 0) / PULSE_TAU)
    // 让回退相位与当前角度对齐，切到回退模式时无跳变
    theta = Math.PI * x + (sideOf(p0.index) === 1 ? 0 : Math.PI)
  } else if (props.isSwinging) {
    // —— 回退：无 Web Audio 时用内部相位钟近似 ——
    const period = props.beatInterval && props.beatInterval > 0 ? props.beatInterval : 0.5
    theta += (Math.PI * dt) / period
    angle = AMPLITUDE * Math.cos(theta)
    const frac = (theta / Math.PI) % 1
    pulse = Math.exp((-frac * period) / PULSE_TAU)
  } else {
    // —— 停摆：指数衰减回中，发光同步淡出 ——
    angle *= Math.exp(-dt / SETTLE_TAU)
    pulse *= Math.exp(-dt / PULSE_TAU)
    if (Math.abs(angle) < 0.05) angle = 0
    if (pulse < 0.01) pulse = 0
    theta = 0
    // 已完全回中：画完最后一帧就停掉 rAF，避免页面空闲时 60fps 白白耗电
    if (angle === 0 && pulse === 0) {
      applyVars()
      rafId = 0
      return
    }
  }

  applyVars()
  rafId = requestAnimationFrame(step)
}

/** 重新播放时唤醒已停转的 rAF；重置 lastTs 防止跨越空窗期的 dt 跳变 */
watch(
  () => props.isSwinging,
  (playing) => {
    if (playing && rafId === 0) {
      lastTs = 0
      rafId = requestAnimationFrame(step)
    }
  },
)

function applyVars() {
  const el = rootEl.value
  if (!el) return
  el.style.setProperty('--angle', `${angle.toFixed(3)}deg`)
  el.style.setProperty('--pulse', pulse.toFixed(3))
}

onMounted(() => {
  applyVars()
  rafId = requestAnimationFrame(step)
})

onUnmounted(() => {
  cancelAnimationFrame(rafId)
})
</script>

<style scoped>
.metronome-visual {
  --angle: 0deg;
  --pulse: 0;
  --pivot: #1f2933;
  --rod: #34495e;
  --weight: #e74c3c;
  --beat-idle: #b7c0c9;
  --beat-accent: #1989fa;
  width: 100%;
  height: 100%;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 摆锤舞台：占据剩余高度，拍点在其下方，二者不重叠 */
.stage {
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 0;
  display: flex;
  justify-content: center;
}

/* 圆弧轨道 */
.arc-track {
  position: absolute;
  bottom: 6%;
  width: min(280px, 86%);
  height: auto;
  pointer-events: none;
}

.arc-track path {
  fill: none;
  stroke: var(--rod);
  stroke-opacity: 0.14;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 3 7;
}

.pivot {
  position: absolute;
  top: 8px;
  left: 50%;
  width: 18px;
  height: 18px;
  margin-left: -9px;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%, #5b6b7b, var(--pivot));
  box-shadow:
    0 0 calc(14px * var(--pulse)) rgba(25, 137, 250, calc(0.55 * var(--pulse))),
    0 2px 5px rgba(0, 0, 0, 0.28);
  z-index: 3;
}

/* 摆杆：高度取舞台的 74%，随舞台自适应，永不下探到拍点区 */
.rod {
  position: absolute;
  top: 17px;
  left: 50%;
  width: 5px;
  height: 74%;
  margin-left: -2.5px;
  transform-origin: top center;
  transform: rotate(var(--angle));
  background: linear-gradient(180deg, #4a6076, var(--rod));
  border-radius: 3px;
  will-change: transform;
}

.weight {
  position: absolute;
  top: 58%;
  left: 50%;
  width: 34px;
  height: 46px;
  margin-left: -17px;
  border-radius: 9px;
  background: linear-gradient(145deg, #ff6b5b, var(--weight));
  box-shadow:
    0 0 calc(26px * var(--pulse)) rgba(231, 76, 60, calc(0.7 * var(--pulse))),
    0 6px 12px rgba(0, 0, 0, 0.22);
}

.weight-line {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 3px;
  height: 62%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.85);
  border-radius: 2px;
}

/* 拍点：固定在底部一行 */
.dots {
  flex: none;
  display: flex;
  justify-content: center;
  gap: clamp(10px, calc(46px - var(--beat-count, 4) * 5px), 30px);
  padding: 10px 0 16px;
}

.dot {
  --glow: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--beat-idle);
  border: 2px solid var(--beat-idle);
  border-radius: 50%;
  transition: color 0.18s ease, border-color 0.18s ease;
}

.dot.accent {
  color: var(--beat-accent);
  border-color: var(--beat-accent);
}

/* 当前拍：随 --pulse 呼吸发光并轻微放大 */
.dot.current {
  --pulse-scale: calc(1 + 0.28 * var(--pulse));
  transform: scale(var(--pulse-scale));
  color: #fff;
  border-color: var(--weight);
  background:
    radial-gradient(circle, rgba(231, 76, 60, calc(0.18 + 0.5 * var(--pulse))), transparent 70%)
    padding-box;
  box-shadow: 0 0 calc(18px * var(--pulse)) rgba(231, 76, 60, calc(0.75 * var(--pulse)));
}

.dot.current.accent {
  border-color: var(--beat-accent);
  background:
    radial-gradient(circle, rgba(25, 137, 250, calc(0.18 + 0.5 * var(--pulse))), transparent 70%)
    padding-box;
  box-shadow: 0 0 calc(18px * var(--pulse)) rgba(25, 137, 250, calc(0.75 * var(--pulse)));
}

/* 移动端 */
@media (max-width: 600px) {
  .metronome-visual {
    min-height: 150px;
  }

  .dot {
    width: 22px;
    height: 22px;
    font-size: 12px;
  }

  .weight {
    width: 28px;
    height: 38px;
    margin-left: -14px;
  }
}

/* 暗色模式 */
@media (prefers-color-scheme: dark) {
  .metronome-visual {
    --pivot: #d9dee3;
    --rod: #8a97a5;
    --beat-idle: #6b7684;
    --beat-accent: #4fa3ff;
  }

  .arc-track path {
    stroke-opacity: 0.2;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dot {
    transition: none;
  }
}
</style>
